import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Spinner } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { createMediaClient, type MediaAssetView } from "./mediaApi";

/** The display URL for a stored asset id (first rendition), or null if it isn't ready. */
export function assetThumb(asset: MediaAssetView | undefined): string | null {
  if (!asset) return null;
  return Object.values(asset.renditions)[0] ?? null;
}

/** Shared query for a venue's media library — deduped across the picker and any thumbnail lookups. */
export function useMediaLibrary(venueId: string) {
  const client = useMemo(() => createMediaClient(venueId), [venueId]);
  return useQuery({
    queryKey: ["venue", venueId, "media", "library"],
    queryFn: () => client.list(),
  });
}

/**
 * Reusable media picker — a thumbnail grid over the venue's media library so a restaurateur picks an
 * image instead of hand-typing an opaque asset UUID (UX review P1). Only ready (rendered) assets show.
 * Single-select: clicking a tile calls [onSelect] and closes. Used for the site logo + gallery.
 */
export function MediaPicker({
  venueId,
  onSelect,
  onClose,
  excludeIds = [],
}: {
  venueId: string;
  onSelect: (assetId: string) => void;
  onClose: () => void;
  excludeIds?: string[];
}) {
  const { t } = useTranslation();
  const library = useMediaLibrary(venueId);
  const excluded = new Set(excludeIds);
  const assets = (library.data ?? []).filter((a) => !excluded.has(a.id) && assetThumb(a) !== null);

  return (
    <div
      className={css({
        position: "fixed",
        inset: "0",
        bg: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: "50",
        p: "4",
      })}
    >
      <div
        className={css({
          bg: "bg.default",
          rounded: "lg",
          boxShadow: "lg",
          p: "5",
          w: "full",
          maxW: "2xl",
          maxH: "80dvh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "3",
        })}
      >
        <div
          className={css({
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          })}
        >
          <h3 className={css({ fontSize: "md", fontWeight: "semibold" })}>
            {t("media.picker.title")}
          </h3>
          <Button size="sm" variant="ghost" onClick={onClose}>
            {t("media.picker.cancel")}
          </Button>
        </div>

        {library.isLoading && <Spinner aria-label={t("media.picker.loading")} />}
        {library.isError && (
          <p role="alert" className={css({ color: "fg.error", fontSize: "sm" })}>
            {t("media.picker.error")}
          </p>
        )}
        {library.data && assets.length === 0 && (
          <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("media.picker.empty")}</p>
        )}

        <div
          className={css({
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: "3",
          })}
        >
          {assets.map((asset) => (
            <button
              key={asset.id}
              type="button"
              onClick={() => {
                onSelect(asset.id);
                onClose();
              }}
              className={css({
                display: "block",
                p: "0",
                borderWidth: "1px",
                borderColor: "border.default",
                rounded: "md",
                overflow: "hidden",
                cursor: "pointer",
                _hover: { borderColor: "colorPalette.default" },
              })}
            >
              <img
                src={assetThumb(asset) ?? ""}
                alt={asset.altText ?? ""}
                className={css({
                  width: "full",
                  aspectRatio: "1",
                  objectFit: "cover",
                  display: "block",
                })}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
